import { CartesianChart } from '@/common/dashboard/charts/CartesianChart';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { Grid } from '@/common/grid/Grid';
import { useDependentKey } from '@/common/hooks/key/useDependentKey';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

export const McdFinTrendChart = ({ formValues, isPaid, isChart }) => {

  const { rbtPeriodId, stateIds, ctrtIds, brandProdIds } = formValues;
  const rsrc = useRsrc();
  const gridRef = useRef();

  const { navigateTo } = useNavState();

  const [data, setData] = useState([]);

  const [fieldDefs, setFieldDefs] = useState();
  const [isGridReady, setIsGridReady] = useState(false);

  const [prodIds, setProdIds] = useState([]);
  const [progIds, setProgIds] = useState([]);

  const theme = useTheme();

  useEffect(() => {
    if (isChart) {
      return;
    }

    const reqParam = {
      periodId: rbtPeriodId
    };

    rsrc.data('sc', 'MCD_FIN_TREND_FIELD_DEFS_DATA_PLUGIN', reqParam).then(fieldDefs => {
      setFieldDefs(fieldDefs);
    });
  }, [isChart, rbtPeriodId]);

  useEffect(() => {
    const reqParam = {
      periodId: rbtPeriodId,
      stateIds,
      ctrtIds,
      brandIds: brandProdIds,
      isPaid,
      isChart
    };

    rsrc.data('sc', 'MCD_FIN_TREND_CHART_DATA_PLUGIN', reqParam).then(data => {
      if (isChart) {
        data.series[0].color = isPaid ? theme.palette.success.main : theme.palette.error.main;
        setProdIds(data.prodIds);
        setProgIds(data.progIds);
      }
      setData(data);
    });

  }, [rbtPeriodId, stateIds, ctrtIds, brandProdIds, isChart]);

  const onGridReady = () => {
    setIsGridReady(true);
  };

  useEffect(() => {
    if (isGridReady && gridRef.current && data) {
      gridRef.current.setDataSource(data.rows);
      gridRef.current.setTotalsRow(data.totalsRow);
    }
  }, [isGridReady, data]);

  const navigateToClaimLineList = (dataPoint) => {
    const rbtPeriodCode = dataPoint.category;
    const rowsReq = {};
    rowsReq.expr = ExprUtils.whereParams('code', rbtPeriodCode);
    rsrc.one('sc', 'PERIOD', rowsReq).then(period => {
      const rbtPeriodId = period.id;
      let expr = ExprUtils.whereParams('rbtPeriodId', rbtPeriodId);

      if (prodIds.length > 0) {
        const prodExpr = ExprUtils.whereParams('prodId', prodIds);
        expr = ExprUtils.and(expr, prodExpr);
      }

      if (stateIds.length > 0) {
        const stateExpr = ExprUtils.whereParams('mcdClaimStateId', stateIds);
        stateExpr.fkEntity = 'MCD_CLAIM';
        stateExpr.fkColPrefix = 'mcd_claim';
        expr = ExprUtils.and(expr, stateExpr);
      }

      if (progIds.length > 0) {
        const progExpr = ExprUtils.whereParams('mcdClaimProgId', progIds);
        progExpr.fkEntity = 'MCD_CLAIM';
        progExpr.fkColPrefix = 'mcd_claim';
        expr = ExprUtils.and(expr, progExpr);
      }

      const navState = {
        mode: Mode.LIST,
        expr: expr
      };
      navigateTo('/sc/mcd-claim-line', navState);
    });
  }

  const gridKey = useDependentKey(fieldDefs);

  return (
    <>
      {isChart && (
        <CartesianChart
          data={data}
          yLabel={isPaid ? 'Paid Dollars' : 'Disputed Dollars'}
          xLabel="Rebate Period"
          allowNegative
          onClick={(dataPoint) => navigateToClaimLineList(dataPoint)}
        />
      )}

      {!isChart && fieldDefs && (
        <Grid
          key={gridKey}
          ref={gridRef}
          gridOptions={{
            fieldDefs,
            rowModelType: 'clientSide',
            statusBar: null
          }}
          events={{ onGridReady }}
        />
      )}
    </>

  );
};
